import React, { useEffect, useMemo, useState } from "react";
import {
  CharacterDiceTemplate,
  CharacterDiceTemplateRequest,
  FateFormula,
} from "../../../types/types";
import { ConfirmationDialog } from "../../ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faDice,
  faEye,
  faEyeSlash,
  faChevronDown,
  faChevronRight,
  faCopy,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import DiceTemplateForm from "./DiceTemplateForm";
import FateFormulaPicker from "../FateFormula/FateFormulaPicker";

export interface DiceTemplateManagerProps {
  templates: CharacterDiceTemplate[];
  onCreateTemplate: (_templateData: CharacterDiceTemplateRequest) => Promise<void>;
  onUpdateTemplate: (
    _templateId: number,
    _templateData: CharacterDiceTemplateRequest
  ) => Promise<void>;
  onDeleteTemplate: (_templateId: number) => Promise<void>;
  loading?: boolean;
  className?: string;
  /** When true, hides add/edit/delete controls — templates are view + roll only. */
  readOnly?: boolean;
  /** Optional: called when the user rolls publicly (via WebSocket). If provided, shows public/private buttons. */
  onRollPublic?: (_template: CharacterDiceTemplate) => void;
  /** Optional: called when the user rolls privately (via WebSocket). If provided, shows public/private buttons. */
  onRollPrivate?: (_template: CharacterDiceTemplate) => void;
  /** Campaign scope for the "Add from Library" picker. When set, the picker also lists this campaign's formulas; when undefined, personal formulas only. */
  campaignId?: number;
}

const DiceTemplateManager: React.FC<DiceTemplateManagerProps> = ({
  templates,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  loading = false,
  className = "",
  readOnly = false,
  onRollPublic,
  onRollPrivate,
  campaignId,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CharacterDiceTemplate | null>(null);
  // When true the form is pre-filled from a source template but submits as a *create* (#132)
  const [duplicating, setDuplicating] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Group templates by skillType (displayed as "Category")
  const groupedTemplates = useMemo(() => {
    const groups = new Map<string, CharacterDiceTemplate[]>();
    templates.forEach((t) => {
      const key = t.skillType || "Other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(t);
    });
    return Array.from(groups.entries());
  }, [templates]);

  useEffect(() => {
    setExpandedCategories(new Set(groupedTemplates.map(([category]) => category)));
  }, [groupedTemplates]);

  const handleCreateTemplate = async (templateData: CharacterDiceTemplateRequest) => {
    setFormLoading(true);
    try {
      await onCreateTemplate(templateData);
      setShowForm(false);
      setEditingTemplate(null);
      setDuplicating(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTemplate = async (templateData: CharacterDiceTemplateRequest) => {
    if (!editingTemplate) return;
    setFormLoading(true);
    try {
      await onUpdateTemplate(editingTemplate.id, templateData);
      setEditingTemplate(null);
      setDuplicating(false);
      setShowForm(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    setDeleteTemplateId(templateId);
  };

  const confirmDeleteTemplate = async () => {
    if (deleteTemplateId !== null) {
      await onDeleteTemplate(deleteTemplateId);
      setDeleteTemplateId(null);
    }
  };

  const cancelDeleteTemplate = () => {
    setDeleteTemplateId(null);
  };

  const startEditTemplate = (template: CharacterDiceTemplate) => {
    setEditingTemplate(template);
    setDuplicating(false);
    setShowForm(true);
  };

  // Open the form pre-filled from a source template; submits as a new template (#132)
  const startDuplicateTemplate = (template: CharacterDiceTemplate) => {
    setEditingTemplate({ ...template, name: `${template.name} (copy)` });
    setDuplicating(true);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setEditingTemplate(null);
    setDuplicating(false);
    setShowForm(false);
  };

  const handlePickFormula = async (f: FateFormula) => {
    await onCreateTemplate({
      name: f.name,
      diceNotation: f.diceNotation,
      modifier: f.modifier,
      description: f.description,
      skillType: f.skillType,
    });
    setShowPicker(false);
  };

  const onRollTemplate = () => {
    // Roll handled by session buttons only
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const hasSessionRoll = !!(onRollPublic || onRollPrivate);

  return (
    <div className={`bg-zinc-900 border border-zinc-800 ${className}`} id="dice-templates-section">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-amber-400 tracking-wide uppercase flex items-center gap-2">
            <FontAwesomeIcon icon={faDice} />
            Fate Formulas
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {hasSessionRoll ? "Roll dice in your active session" : "Quick dice rolls for the hero"}
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            {/* ponytail: show picker whenever editable — campaignId undefined means personal-formulas-only picker */}
            <button
              onClick={() => setShowPicker(true)}
              disabled={loading}
              className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faBookOpen} className="mr-1" />
              Add from Library
            </button>
            <button
              onClick={() => setShowForm(true)}
              disabled={loading || showForm}
              className="px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Add Template
            </button>
          </div>
        )}
      </div>

      {/* Inline Form */}
      {showForm && (
        <div className="px-6 py-4 border-b border-zinc-800">
          <DiceTemplateForm
            template={editingTemplate || undefined}
            isEditing={!!editingTemplate && !duplicating}
            onSave={!!editingTemplate && !duplicating ? handleUpdateTemplate : handleCreateTemplate}
            onCancel={handleCancelForm}
            loading={formLoading}
          />
        </div>
      )}

      {/* Templates grouped by Category */}
      {templates.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <FontAwesomeIcon icon={faDice} className="text-zinc-600 text-3xl mb-3" />
          <p className="text-zinc-400">No fate formulas yet</p>
          <p className="text-zinc-500 text-sm mt-1">
            Create your first fate formula to quickly roll dice for various actions
          </p>
        </div>
      ) : (
        <div>
          {groupedTemplates.map(([category, categoryTemplates]) => {
            const isExpanded = expandedCategories.has(category);
            return (
              <div key={category}>
                {/* Category header (accordion toggle) */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-6 py-2 bg-zinc-800/40 border-b border-zinc-800 flex items-center gap-2 hover:bg-zinc-800/60 transition-colors"
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    className="text-zinc-500 text-[10px]"
                  />
                  <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                    {category}
                  </span>
                  <span className="ml-auto text-xs text-zinc-600">{categoryTemplates.length}</span>
                </button>
                {/* Rows — visible only when expanded */}
                {isExpanded && (
                  <div className="divide-y divide-zinc-800">
                    {categoryTemplates.map((template) => (
                      <DiceTemplateRow
                        key={template.id}
                        template={template}
                        onEdit={() => startEditTemplate(template)}
                        onDuplicate={() => startDuplicateTemplate(template)}
                        onDelete={() => handleDeleteTemplate(template.id)}
                        onRoll={onRollTemplate}
                        onRollPublic={onRollPublic ? () => onRollPublic(template) : undefined}
                        onRollPrivate={onRollPrivate ? () => onRollPrivate(template) : undefined}
                        disabled={loading || showForm}
                        readOnly={readOnly}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Dialog for Template Deletion */}
      <ConfirmationDialog
        isOpen={deleteTemplateId !== null}
        title="Delete Fate Formula"
        message="Are you sure you want to delete this fate formula?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteTemplate}
        onCancel={cancelDeleteTemplate}
        variant="danger"
      />

      {/* Fate Formula Picker — copy a library formula into this entity's templates */}
      {showPicker && (
        <FateFormulaPicker
          campaignId={campaignId}
          onPick={handlePickFormula}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
};

export default DiceTemplateManager;

// Individual Dice Template Row Component
interface DiceTemplateRowProps {
  template: CharacterDiceTemplate;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRoll: () => void;
  onRollPublic?: () => void;
  onRollPrivate?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
}

const DiceTemplateRow: React.FC<DiceTemplateRowProps> = ({
  template,
  onEdit,
  onDuplicate,
  onDelete,
  onRoll,
  onRollPublic,
  onRollPrivate,
  disabled = false,
  readOnly = false,
}) => {
  // Extract the die face from notation: "1d20" → "d20", "2d6" → "d6"
  const diceLabel = template.diceNotation.match(/d\d+/i)?.[0] || template.diceNotation;
  const hasSessionRoll = !!(onRollPublic || onRollPrivate);

  return (
    <div className="px-6 py-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors">
      <div className="flex items-center gap-4 min-w-0">
        {/* Dice badge */}
        <div className="w-10 h-10 bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 font-mono text-xs font-bold flex-shrink-0">
          {diceLabel}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-white">{template.name}</p>
          <p className="text-xs text-zinc-500 flex items-center gap-1 flex-wrap">
            <span>{template.diceNotation}</span>
            {template.modifier !== 0 && (
              <span>{template.modifier > 0 ? `+${template.modifier}` : template.modifier}</span>
            )}
          </p>
          {template.description && (
            <p className="text-xs text-zinc-600 mt-0.5 truncate">{template.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
        {/* Session roll buttons (public / private) — shown when WebSocket connected */}
        {hasSessionRoll && (
          <>
            {onRollPublic && (
              <button
                onClick={onRollPublic}
                disabled={disabled}
                title="Roll publicly — all session participants see this"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faEye} className="text-[10px]" />
                Public
              </button>
            )}
            {onRollPrivate && (
              <button
                onClick={onRollPrivate}
                disabled={disabled}
                title="Roll privately — only you see this"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 border border-zinc-600 text-zinc-300 hover:bg-zinc-600 transition-colors disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faEyeSlash} className="text-[10px]" />
                Private
              </button>
            )}
          </>
        )}
        {!readOnly && (
          <>
            {!hasSessionRoll && (
              <button
                onClick={onRoll}
                disabled={disabled}
                title="Roll Template"
                className="px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
              >
                Roll
              </button>
            )}
            <button
              onClick={onEdit}
              disabled={disabled}
              title="Edit Template"
              className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              onClick={onDuplicate}
              disabled={disabled}
              title="Duplicate Template"
              className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-400 hover:bg-zinc-700 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
            <button
              onClick={onDelete}
              disabled={disabled}
              title="Delete Template"
              className="px-3 py-1.5 text-xs bg-red-900/40 border border-red-800/60 text-red-400 hover:bg-red-900/60 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
